<script setup>
import { computed, ref } from 'vue'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import InputText from '../../../../../../../resources/js/components/umisis/form/inputs/InputText.vue'
import { Field, FieldContent, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { Switch } from '@/components/ui/switch/index.js'
import { useChecklistTemplatesStore } from '../../stores/checklistTemplatesStore.js'

const props = defineProps({
  checklistTemplate: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['updated'])
const { componentRef: dialog, openModal, closeModal } = useModalControl()
const checklistTemplatesStore = useChecklistTemplatesStore('edit-details-modal')

const title = ref('')
const description = ref('')
const isPublic = ref(false)
const loading = ref(false)
const errorMessage = ref(null)

const checklistTemplateId = computed(() => Number(props.checklistTemplate?.id ?? 0))
const canSubmit = computed(() => {
  return !loading.value
    && checklistTemplateId.value > 0
    && props.checklistTemplate?.status === 'draft'
    && title.value.trim().length > 0
})

const fieldControlBaseClass = 'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'

const onShow = () => {
  title.value = props.checklistTemplate?.title ?? ''
  description.value = props.checklistTemplate?.description ?? ''
  isPublic.value = Boolean(props.checklistTemplate?.public)
  errorMessage.value = null
}

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  errorMessage.value = null

  if (!canSubmit.value) {
    errorMessage.value = 'A checklist template name is required.'
    return
  }

  loading.value = true

  try {
    const updatedChecklistTemplate = await checklistTemplatesStore.itemPatch(checklistTemplateId.value, {
      title: title.value.trim(),
      description: description.value.trim().length > 0 ? description.value.trim() : null,
      public: isPublic.value,
    })

    emit('updated', updatedChecklistTemplate)
    closeModal()
  } catch (error) {
    errorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to update checklist template.'
  } finally {
    loading.value = false
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>

<template>
  <UmisisModal
    ref="dialog"
    title="Edit Checklist Template"
    description="Update checklist template name and description."
    ok-title="Save"
    cancel-title="Cancel"
    :ok-disabled="!canSubmit"
    @ok="handleOk"
    @show="onShow"
  >
    <div class="flex flex-col gap-4">
      <InputText
        v-model="title"
        label="Name"
        placeholder="Checklist template name"
      />

      <Field>
        <FieldLabel for="checklist-template-edit-description">
          Description
        </FieldLabel>
        <textarea
          id="checklist-template-edit-description"
          v-model="description"
          rows="4"
          :class="fieldControlBaseClass"
          placeholder="Describe when this checklist template should be used..."
        />
      </Field>

      <Field orientation="horizontal" class="items-start rounded-md border p-3">
        <FieldContent>
          <FieldLabel for="checklist-template-edit-public">
            Public template
          </FieldLabel>
          <FieldDescription>
            Other users can view and duplicate this template, but they cannot edit it.
          </FieldDescription>
        </FieldContent>
        <Switch id="checklist-template-edit-public" v-model="isPublic" class="shrink-0" :disabled="loading" />
      </Field>

      <FieldDescription v-if="errorMessage" class="text-destructive font-medium">
        {{ errorMessage }}
      </FieldDescription>
    </div>
  </UmisisModal>
</template>
